import { API_URLS } from '../urls';

/**
 * Annonce « Je suis en direct » sur Facebook et Instagram (2026-10-17) : au
 * passage à l'antenne, admin-api publie une carte EN DIRECT sur la Page Facebook
 * et le compte Instagram que le créateur a reliés dans sa fiche. Réglage par
 * chaîne, à chaque passage à l'antenne (aucune restriction de temps). admin-api,
 * LiveAnnounceController (contrôleur Symfony pur : `application/json` accepté).
 */

export type LiveAnnounceNetwork = 'facebook' | 'instagram';

export interface LiveAnnounceNetworkResult {
  ok    : boolean;
  url   : string | null;
  error : string | null;
}

export interface LiveAnnounceState {
  channelId       : number;
  enabled         : boolean;
  networks        : LiveAnnounceNetwork[];
  /** Texte du créateur ; null = texte par défaut. */
  text            : string | null;
  defaultText     : string;
  textMaxLength   : number;
  /** Adresse du direct sur le portail, kleon.tv/{slug}. */
  link            : string;
  /** Le texte tel qu'il partirait maintenant (programme en cours, adresse). */
  preview         : string;
  accounts        : Record<LiveAnnounceNetwork, { linked: boolean; label: string | null }>;
  announcedAt     : string | null;
  lastResult      : {
    at       : string;
    status   : 'pending' | 'done';
    networks : Partial<Record<LiveAnnounceNetwork, LiveAnnounceNetworkResult>>;
  } | null;
}

export interface LiveAnnounceUpdate {
  enabled?  : boolean;
  networks? : LiveAnnounceNetwork[];
  text?     : string | null;
}

export class LiveAnnounceError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

const url = (key: 'liveAnnounce' | 'liveAnnounceCard', channelId: number) =>
  API_URLS[key].replace('{channelId}', String(channelId));

const readJson = async (res: Response): Promise<LiveAnnounceState> => {
  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new LiveAnnounceError(data.error ?? `HTTP ${res.status}`, res.status);

  return data as LiveAnnounceState;
};

export const fetchLiveAnnounce = async (adminId: number, channelId: number): Promise<LiveAnnounceState> =>
  readJson(await fetch(`${url('liveAnnounce', channelId)}?adminId=${adminId}`, { cache: 'no-store' }));

export const updateLiveAnnounce = async (adminId: number, channelId: number, change: LiveAnnounceUpdate): Promise<LiveAnnounceState> =>
  readJson(await fetch(url('liveAnnounce', channelId), {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify({ adminId, ...change }),
  }));

/**
 * Suit l'annonce partie au passage à l'antenne, pour la régie : relit le réglage
 * toutes les 4 s pendant 3 min au plus, et appelle `onDone` une fois, quand une
 * annonce lancée depuis `since` a son résultat. Rien n'est appelé si aucune
 * annonce n'est partie (réglage coupé, aucun compte relié).
 * Rend une fonction qui arrête le suivi.
 */
export const followLiveAnnounce = (
  adminId   : number,
  channelId : number,
  since     : Date,
  onDone    : (result: NonNullable<LiveAnnounceState['lastResult']>) => void,
): (() => void) => {
  let stopped  = false;
  let timer    = 0;
  const giveUp = Date.now() + 3 * 60_000;

  const check = async () => {
    if (stopped) return;
    try {
      const state  = await fetchLiveAnnounce(adminId, channelId);
      const result = state.lastResult;
      // Le serveur pose la réservation (status 'pending') pendant la bascule : un
      // résultat plus ancien que le clic n'est pas celui de ce passage à l'antenne.
      const ours   = result && new Date(result.at).getTime() >= since.getTime() - 60_000;

      if (!state.enabled || !ours) return;
      if (result!.status === 'done') { onDone(result!); return; }
    } catch { /* relevé suivant */ }

    if (!stopped && Date.now() < giveUp) timer = window.setTimeout(check, 4000);
  };

  timer = window.setTimeout(check, 2000);

  return () => { stopped = true; window.clearTimeout(timer); };
};

/** Aperçu de la carte, dessinée par le serveur ; `version` force la relecture. */
export const liveAnnounceCardUrl = (adminId: number, channelId: number, version: number) =>
  `${url('liveAnnounceCard', channelId)}?adminId=${adminId}&v=${version}`;
